import { FC, useCallback, useEffect, useRef } from "react";
import { cn } from "~/lib/utils";

// Canvas size constants
const WIDTH = 1000;
const HEIGHT = 400;

// Shuffle frequencies so that neighbors are not too similar
const shuffle = [1, 3, 0, 4, 2];

// Audio visualization options
const opts = {
  smoothing: 0.9,
  fft: 8,
  minDecibels: -70,
  scale: 0.7,
  glow: 10,
  color1: [148, 191, 241],
  color2: [131, 237, 203],
  color3: [64, 139, 227],
  fillOpacity: 0.4,
  lineWidth: 1,
  blend: "screen" as GlobalCompositeOperation,
  shift: 70,
  width: 90,
  amp: 1,
};

interface AudioVisualizerProps {
  isActive: boolean;
  className?: string;
  audioStream?: MediaStream | null;
}

export const AudioVisualizer: FC<AudioVisualizerProps> = ({
  isActive,
  className,
  audioStream,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const freqsRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isActiveRef = useRef<boolean>(false);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  const range = useCallback((i: number) => {
    return Array.from(Array(i).keys());
  }, []);

  // Pick a frequency for the given channel and value index
  const freq = useCallback((channel: number, i: number) => {
    const band = 2 * channel + shuffle[i] * 6;
    return freqsRef.current ? freqsRef.current[band] : 0;
  }, []);

  // Returns the scale factor for the given value index
  const scale = useCallback((i: number) => {
    const x = Math.abs(2 - i);
    const s = 3 - x;
    return (s / 3) * opts.amp;
  }, []);

  // Animation state for default line
  const animationTimeRef = useRef<number>(0);

  // Draw animated default lines (four wavy lines) when no audio
  const drawDefaultLine = useCallback(
    (ctx: CanvasRenderingContext2D, animated: boolean = false) => {
      const m = HEIGHT / 2;

      ctx.globalCompositeOperation = "source-over";

      if (!animated) {
        animationTimeRef.current = 0;
        ctx.strokeStyle = "rgba(148, 191, 241, 0.7)";
        ctx.lineWidth = 3;
        ctx.shadowBlur = opts.glow;
        ctx.shadowColor = "rgba(148, 191, 241, 0.45)";
        ctx.beginPath();
        ctx.moveTo(0, m);
        ctx.lineTo(WIDTH, m);
        ctx.stroke();
        return;
      }

      // much slower animation: advance time more gently for a slower feel
      animationTimeRef.current += 0.003;

      const time = animationTimeRef.current;

      // Parameters for the 3 lines
      const lines = 3;
      const spacing = 2; // closer vertical spacing between lines
      const baseAmplitude = 7; // increased amplitude for more pronounced waves
      const xStep = 2; // resolution for smoother curves
      const widths = [15, 12, 10]; // line widths set to 15, 12, 10

      // For each line, draw a wavy path with its own phase and color
      for (let li = 0; li < lines; li++) {
        // phase shift so lines are staggered
        const phase = li * 1.2;
        // vertical offset that moves up and down over time - more floating
        const verticalWave = Math.sin(time * 0.6 + li * 0.7) * 15;
        const lineYOffset = (li - (lines - 1) / 2) * spacing + verticalWave;

        // animated gradient per line mixing the provided colors
        const mix = (a: number[], b: number[], t: number) =>
          a.map((v, i) => Math.round(v * (1 - t) + b[i] * t));

        // use a much slower time base for color mixing so gradients change more smoothly
        const colorTime = time * 0.08;
        const tColor = (Math.sin(colorTime + li * 0.4) + 1) / 2;

        const cA = mix(opts.color1, opts.color2, (tColor + li * 0.03) % 1);
        const cB = mix(
          opts.color2,
          opts.color3,
          (tColor * 0.5 + li * 0.06) % 1
        );
        const cC = mix(
          opts.color3,
          opts.color1,
          (tColor * 0.3 + li * 0.09) % 1
        );

        const gradient = ctx.createLinearGradient(0, 0, WIDTH, 0);
        const off1 = Math.max(
          0,
          Math.min(1, (Math.sin(colorTime * 0.4 + li) + 1) / 2)
        );
        const off2 = Math.max(
          0,
          Math.min(1, 0.45 + (Math.cos(colorTime * 0.3 + li) + 1) / 4)
        );

        gradient.addColorStop(0, `rgba(${cA.join(",")}, 0.95)`);
        gradient.addColorStop(off1, `rgba(${cB.join(",")}, 0.6)`);
        gradient.addColorStop(off2, `rgba(${cC.join(",")}, 0.35)`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = widths[li] || 6;
        ctx.shadowBlur = 10;
        ctx.shadowColor = `rgba(${opts.color2.join(",")}, 0.45)`;

        ctx.beginPath();
        let first = true;
        for (let x = 0; x <= WIDTH; x += xStep) {
          const freq = 0.012 + li * 0.002;
          const speed = 0.4 + li * 0.04;
          const amplitude = baseAmplitude;
          const wave = Math.sin(x * freq + time * speed + phase) * amplitude;
          const y = m + lineYOffset + wave;

          if (first) {
            ctx.moveTo(x, y);
            first = false;
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }
    },
    []
  );

  // Draw visualization path
  const drawPath = useCallback(
    (channel: number, ctx: CanvasRenderingContext2D) => {
      const color = opts[
        `color${channel + 1}` as keyof typeof opts
      ] as number[];

      ctx.fillStyle = `rgba(${color.join(",")}, ${opts.fillOpacity})`;
      ctx.strokeStyle = ctx.shadowColor = `rgb(${color.join(",")})`;

      ctx.lineWidth = opts.lineWidth;
      ctx.shadowBlur = opts.glow;
      ctx.globalCompositeOperation = opts.blend;

      const m = HEIGHT / 2;
      const offset = (WIDTH - 15 * opts.width) / 2;

      const x = range(15).map(
        (i) => offset + channel * opts.shift + i * opts.width
      );

      const y = range(5).map((i) =>
        Math.max(0, m - scale(i) * freq(channel, i))
      );

      const h = 2 * m;

      ctx.beginPath();
      ctx.moveTo(0, m);
      ctx.lineTo(x[0], m + 1);

      ctx.bezierCurveTo(x[1], m + 1, x[2], y[0], x[3], y[0]);
      ctx.bezierCurveTo(x[4], y[0], x[4], y[1], x[5], y[1]);
      ctx.bezierCurveTo(x[6], y[1], x[6], y[2], x[7], y[2]);
      ctx.bezierCurveTo(x[8], y[2], x[8], y[3], x[9], y[3]);
      ctx.bezierCurveTo(x[10], y[3], x[10], y[4], x[11], y[4]);

      ctx.bezierCurveTo(x[12], y[4], x[12], m, x[13], m);

      ctx.lineTo(WIDTH, m + 1);
      ctx.lineTo(x[13], m - 1);

      ctx.bezierCurveTo(x[12], m, x[12], h - y[4], x[11], h - y[4]);
      ctx.bezierCurveTo(x[10], h - y[4], x[10], h - y[3], x[9], h - y[3]);
      ctx.bezierCurveTo(x[8], h - y[3], x[8], h - y[2], x[7], h - y[2]);
      ctx.bezierCurveTo(x[6], h - y[2], x[6], h - y[1], x[5], h - y[1]);
      ctx.bezierCurveTo(x[4], h - y[1], x[4], h - y[0], x[3], h - y[0]);
      ctx.bezierCurveTo(x[2], h - y[0], x[1], m, x[0], m);

      ctx.lineTo(0, m);

      ctx.fill();
      ctx.stroke();
    },
    [range, scale, freq]
  );

  // Visualization animation loop
  const visualize = useCallback(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    canvasRef.current.width = WIDTH;
    canvasRef.current.height = HEIGHT;

    // If we have audio data, draw the visualization
    if (analyserRef.current && freqsRef.current) {
      analyserRef.current.smoothingTimeConstant = opts.smoothing;
      analyserRef.current.fftSize = Math.pow(2, opts.fft);
      analyserRef.current.minDecibels = opts.minDecibels;
      analyserRef.current.maxDecibels = 0;

      const freqData = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(freqData);
      freqsRef.current = freqData;

      // Draw three curves (R/G/B)
      drawPath(0, ctx);
      drawPath(1, ctx);
      drawPath(2, ctx);
    } else {
      // Draw animated default line when no audio but active
      drawDefaultLine(ctx, true);
    }

    if (isActiveRef.current) {
      animationFrameRef.current = requestAnimationFrame(visualize);
    }
  }, [drawPath, drawDefaultLine]);

  // Initialize audio visualization
  const initializeVisualization = useCallback(
    async (stream: MediaStream) => {
      if (!canvasRef.current) return;

      try {
        if (audioContextRef.current) {
          await audioContextRef.current.close();
        }

        audioContextRef.current = new AudioContext();

        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }

        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = Math.pow(2, opts.fft);
        analyserRef.current.smoothingTimeConstant = opts.smoothing;
        analyserRef.current.minDecibels = opts.minDecibels;
        analyserRef.current.maxDecibels = 0;

        freqsRef.current = new Uint8Array(
          analyserRef.current.frequencyBinCount
        );

        const input = audioContextRef.current.createMediaStreamSource(stream);
        input.connect(analyserRef.current);

        // Start visualization
        if (isActiveRef.current) {
          visualize();
        }
      } catch (error) {
        // Error initializing audio visualization - silently fail
      }
    },
    [visualize]
  );

  // Stop audio visualization
  const stopVisualization = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, WIDTH, HEIGHT);
      }
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  // Draw the initial default line when component mounts
  const drawInitialLine = useCallback(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    canvasRef.current.width = WIDTH;
    canvasRef.current.height = HEIGHT;

    // draw a static baseline line
    drawDefaultLine(ctx, false);
  }, [drawDefaultLine]);

  useEffect(() => {
    if (isActive && audioStream) {
      initializeVisualization(audioStream);
    } else if (isActive && !audioStream) {
      if (isActiveRef.current) {
        visualize();
      }
    } else if (!isActive) {
      stopVisualization();
      drawInitialLine();
    }
  }, [
    isActive,
    audioStream,
    initializeVisualization,
    stopVisualization,
    visualize,
    drawInitialLine,
  ]);

  useEffect(() => {
    drawInitialLine();
  }, [drawInitialLine]);

  useEffect(() => {
    return () => {
      stopVisualization();
    };
  }, [stopVisualization]);

  return (
    <canvas
      ref={canvasRef}
      className={cn(
        "canvas transition-opacity duration-500 opacity-100",
        className
      )}
      style={{
        width: "100%",
        height: "400px",
        pointerEvents: "none",
        zIndex: 1,
      }}
      width={WIDTH}
      height={HEIGHT}
    />
  );
};
